// src/provider/ApiTokenProvider.tsx
'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { kazeFetch } from '@/utils/kazeFetch';
import { toast } from 'sonner';
import {
  createApiTokenEndpoint,
  getApiTokenEndpoint,
  deleteApiTokenEndpoint,
  getApiTokenUsageEndpoint,
} from '@/config/endpoints';
import { useUserInfo } from '@/provider/UserInfoProvider';
import { useSignupModal } from '@/zustand/store';

export type TokenUsage = {
  has_token: boolean;
  limit: number;
  usage: number;
  remaining: number;
};

type ApiTokenContextType = {
  apiToken: string;
  showApiToken: boolean;
  apiTokenUsage: TokenUsage;
  loadingApiTokenUsage: boolean;
  getApiToken: () => Promise<void>;
  createApiToken: () => Promise<void>;
  deleteApiToken: () => Promise<void>;
  copyToken: () => void;
  showHideApiToken: () => void;
  getApiTokenUsage: () => Promise<void>;
  resetTokenData: () => void;
};

const ApiTokenContext = createContext<ApiTokenContextType>({
  apiToken: '',
  showApiToken: true,
  apiTokenUsage: { has_token: false, limit: 0, usage: 0, remaining: 0 },
  loadingApiTokenUsage: false,
  getApiToken: () => Promise.resolve(),
  createApiToken: () => Promise.resolve(),
  deleteApiToken: () => Promise.resolve(),
  copyToken: () => {},
  showHideApiToken: () => {},
  getApiTokenUsage: () => Promise.resolve(),
  resetTokenData: () => {},
});

export const useApiToken = () => {
  const context = useContext(ApiTokenContext);
  if (!context) {
    throw new Error('useApiToken must be used within an ApiTokenProvider');
  }
  return context;
};

export function ApiTokenProvider({ children }: { children: React.ReactNode }) {
  const { userIdentity } = useUserInfo();
  const isLogin = userIdentity === 3;
  const setSignupModalOpen = useSignupModal((s) => s.setSignupModalOpen);

  const [apiToken, setApiToken] = useState<string>('');
  const [showApiToken, setShowApiToken] = useState<boolean>(true);
  const [apiTokenUsage, setApiTokenUsage] = useState<TokenUsage>({
    has_token: false,
    limit: 0,
    usage: 0,
    remaining: 0,
  });
  const [loadingApiTokenUsage, setLoadingApiTokenUsage] =
    useState<boolean>(false);

  // 查询API Token
  const getApiToken = useCallback(async () => {
    try {
      const res = await kazeFetch(getApiTokenEndpoint, { method: 'POST' });
      setApiToken(res.token);
    } catch (error) {
      toast.error('Failed to get API token, please try again');
    }
  }, []);

  // 新建token
  const createApiToken = async () => {
    try {
      if (!isLogin) {
        setSignupModalOpen(true, {
          signupSuccess: async () => {
            const res = await kazeFetch(createApiTokenEndpoint, {
              method: 'POST',
            });
            setApiToken(res.token);
            getApiTokenUsage();
          },
          description: 'Sign up to get your api token!',
        });
      } else {
        const res = await kazeFetch(createApiTokenEndpoint, { method: 'POST' });
        setApiToken(res.token);
        getApiTokenUsage();
      }
    } catch (error) {
      toast.error('Failed to create API token, please try again');
    }
  };

  // 删除token
  const deleteApiToken = async () => {
    try {
      const res = await kazeFetch(deleteApiTokenEndpoint, { method: 'POST' });
      setApiToken(res.token);
    } catch (error) {
      toast.error('Failed to delete API token, please try again');
    }
  };

  // 复制token
  const copyToken = () => {
    navigator.clipboard.writeText(apiToken);
    toast.success('Token copied successfully');
  };

  const showHideApiToken = () => {
    setShowApiToken(!showApiToken);
  };

  const getApiTokenUsage = useCallback(async () => {
    try {
      setLoadingApiTokenUsage(true);
      const res = await kazeFetch(getApiTokenUsageEndpoint, { method: 'POST' });
      setApiTokenUsage(res);
    } catch (error) {
      toast.error('Failed to get API token usage, please try again');
    } finally {
      setLoadingApiTokenUsage(false);
    }
  }, []);

  // 重置token数据
  const resetTokenData = () => {
    setApiToken('');
    setShowApiToken(true);
    setApiTokenUsage({ has_token: false, limit: 0, usage: 0, remaining: 0 });
  };

  useEffect(() => {
    if (isLogin) {
      if (!apiToken) {
        getApiToken();
      }
      getApiTokenUsage();
    } else {
      resetTokenData();
    }
  }, [getApiToken, getApiTokenUsage, isLogin, apiToken]);

  const value = {
    apiToken,
    showApiToken,
    apiTokenUsage,
    loadingApiTokenUsage,
    getApiToken,
    createApiToken,
    deleteApiToken,
    copyToken,
    showHideApiToken,
    getApiTokenUsage,
    resetTokenData,
  };

  return (
    <ApiTokenContext.Provider value={value}>
      {children}
    </ApiTokenContext.Provider>
  );
}
